/**
 * RETAILY bundle tiers — count-based cart watcher.
 *
 * Ported from assets/tm-shipping-progress-bar.js (count mode): watches the
 * cart via /cart.js (in-flight dedupe) + Horizon's standard cart event, counts
 * items matching the configured product type, and drives the tier card UI.
 *
 * Tier numbers are fixed by design decision: 2 = −10%, 3 = −15% + free
 * shipping, 4+ = −20%. Only message texts are configurable (data attributes).
 * Display only — actual discounts live in Shopify admin automatic discounts.
 *
 * Two render modes share this element (both driven by the same events):
 * - Card (product page / sections): tier cards, progress track, [data-bt-msg].
 *   Until the first tier is reached ALL tiers show the highlighted "next"
 *   style so the whole deal is visible.
 * - Slim (cart drawer, `data-slim` attr): 3 segment bars + labels +
 *   [data-bt-nudge] one-liner (templates in data-nudge0/1/2/3 and
 *   data-nudge-best, design ref Techmerch Cart.html). Slim uses the plain
 *   state machine — only the actual next tier is highlighted.
 *
 * IMPORTANT: never patch window.fetch to detect cart mutations. Littledata
 * (and other pixel apps) wrap fetch too, and a second wrapper caused
 * duplicate checkout_started pixel events in production (see the comment in
 * tm-shipping-progress-bar.js). This component is purely passive: it listens
 * to Horizon's own events and only ever issues plain GET /cart.js reads.
 */
(() => {
  const TIERS = [2, 3, 4];
  const FREE_SHIPPING_QTY = 3;

  let inflightCart = null;

  function fetchCart() {
    if (!inflightCart) {
      inflightCart = fetch('/cart.js', { credentials: 'same-origin' })
        .then((response) => response.json())
        .finally(() => {
          inflightCart = null;
        });
    }
    return inflightCart;
  }

  function fillTemplate(template, values) {
    return (template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));
  }

  class RetailyBundleTiers extends HTMLElement {
    connectedCallback() {
      this.productType = (this.dataset.productType || '').toLowerCase().trim();

      if (!this.__retailyBound) {
        this.__retailyBound = true;
        // Horizon (product-form.js etc.) dispatches the standard cart event
        // BEFORE its fetch to the cart endpoint — the event carries a promise
        // that resolves when the request completes. Refreshing immediately
        // would read the OLD cart, so wait for the promise when present.
        this._onCartEvent = (event) => {
          const pending = event?.promise || event?.detail?.promise;
          if (pending && typeof pending.then === 'function') {
            pending.then(() => this.refresh()).catch(() => this.refresh());
          } else {
            this.refresh();
          }
        };
        // The cart drawer additionally dispatches tm:cart-drawer-opened
        // (TECHMERCH edit in assets/cart-drawer.js) which we reuse.
        document.addEventListener('shopify:cart:lines-update', this._onCartEvent);
        document.addEventListener('cart:update', this._onCartEvent);
        document.addEventListener('tm:cart-drawer-opened', this._onCartEvent);
        document.addEventListener('shopify:section:load', this._onCartEvent);
      }

      // Guard against Shopify section morphing overwriting JS-rendered
      // message/state: observe own subtree, re-render on external writes.
      // disconnect/reconnect (not a flag) because observer callbacks are
      // microtasks and would see our own writes after the flag is cleared.
      if (!this._observer) {
        this._observer = new MutationObserver(() => {
          clearTimeout(this._observerTimer);
          this._observerTimer = setTimeout(() => this.render(), 50);
        });
      }

      this.refresh();
    }

    disconnectedCallback() {
      this._observer?.disconnect();
    }

    _pauseObserver() {
      this._observer?.disconnect();
    }

    _resumeObserver() {
      this._observer?.observe(this, { childList: true, characterData: true, subtree: true });
    }

    refresh() {
      fetchCart()
        .then((cart) => {
          this.count = (cart.items || []).reduce((sum, item) => {
            const type = (item.product_type || '').toLowerCase().trim();
            if (!this.productType || type === this.productType) return sum + item.quantity;
            return sum;
          }, 0);
          this.render();
        })
        .catch(() => {});
    }

    render() {
      if (typeof this.count !== 'number') return;
      this._pauseObserver();

      const qty = this.count;

      const fill = this.querySelector('[data-bt-fill]');
      if (fill) fill.style.width = `${Math.min(qty / FREE_SHIPPING_QTY, 1) * 100}%`;

      const ship = this.querySelector('[data-bt-ship]');
      if (ship) ship.classList.toggle('is-hit', qty >= FREE_SHIPPING_QTY);

      const activeIdx = TIERS.reduce((acc, min, i) => (qty >= min ? i : acc), -1);
      const nextIdx = TIERS.findIndex((min) => qty < min);
      // Until the first tier is reached, ALL tiers stay in the highlighted
      // "next" style — the whole bundle deal should be clearly visible.
      // (Card mode only — slim segments must show the true state machine.)
      const noneReached = activeIdx === -1 && !this.hasAttribute('data-slim');
      this.querySelectorAll('[data-bt-tier]').forEach((tier, i) => {
        tier.classList.toggle('is-completed', activeIdx > i);
        tier.classList.toggle('is-active', activeIdx === i);
        tier.classList.toggle('is-next', noneReached || (activeIdx < i && nextIdx === i));
        tier.classList.toggle('is-locked', !noneReached && activeIdx < i && nextIdx !== i);
      });

      const msg = this.querySelector('[data-bt-msg]');
      if (msg) {
        let template;
        if (qty === 0) template = this.dataset.msgEmpty;
        else if (qty < FREE_SHIPPING_QTY) template = this.dataset.msgProgress;
        else if (qty < 4) template = this.dataset.msgUnlocked;
        else template = this.dataset.msgBest;
        msg.textContent = fillTemplate(template, { count: qty, remaining: Math.max(FREE_SHIPPING_QTY - qty, 0) });
      }

      // Slim nudge: one short line per cart quantity (0/1/2/3, best at 4+).
      const nudge = this.querySelector('[data-bt-nudge]');
      if (nudge) {
        const template = qty >= 4 ? this.dataset.nudgeBest : this.dataset[`nudge${qty}`];
        nudge.textContent = fillTemplate(template || '', { count: qty });
        nudge.classList.toggle('is-best', qty >= 4);
      }

      this._resumeObserver();
    }
  }

  if (!customElements.get('retaily-bundle-tiers')) {
    customElements.define('retaily-bundle-tiers', RetailyBundleTiers);
  }
})();
